import { Selector } from '@ngxs/store';
import _ from 'lodash';
import { ConversationTypeRemoteDto } from 'src/generated/signalR/conversation-type-remote-dto';
import { IciFlags } from 'src/generated/signalR/ici-flags';
import { SamwinCallInfoRemoteDto } from 'src/generated/signalR/samwin-call-info-remote-dto';
import { SkillUserConfigurationRemoteDto } from 'src/generated/signalR/skill-user-configuration-remote-dto';
import { StatusType } from 'src/generated/signalR/status-type';
import { UserStatusInfoRemoteDto } from 'src/generated/signalR/user-status-info-remote-dto';
import { IShortCallInfo } from '../samwin-lines/samwin-lines.state';
import { SamwinSkillsState } from '../samwin-skills/samwin-skills.state';
import { SamwinUserStatusState } from '../samwin-user-status/samwin-user-status.state';
import { SamwinCallListState } from './samwin-call-list.state';

export class SamwinCallListSelector {
   @Selector([SamwinCallListState.getCallList])
   public static getFirstNonParkedItem(callList: SamwinCallInfoRemoteDto[]): SamwinCallInfoRemoteDto {
      const nonParkedItem = _(callList)
         .filter((call) => !(call.ici.ici & (IciFlags.PARK_PRIVATE | IciFlags.PARK_PUBLIC)))
         .value();
      const firstNonParkedItem = _.head(nonParkedItem);
      if (!_.isNil(firstNonParkedItem)) {
         return firstNonParkedItem;
      }
      return null;
   }

   @Selector([
      SamwinCallListSelector.getFirstNonParkedItem,
      SamwinSkillsState.getIsSharedSkillFn,
      SamwinUserStatusState.getCurrentStatusInfo,
      SamwinSkillsState.getSkillUserConfigurationWithIdFn,
   ])
   public static getActiveOrRingingCallFromSharedSkill(
      firstItem: SamwinCallInfoRemoteDto,
      getIsSharedSkillFn,
      currentStatusInfo: UserStatusInfoRemoteDto,
      getSkillUserConfigurationWithIdFn: (skillId: number) => SkillUserConfigurationRemoteDto
   ): IShortCallInfo {
      if (_.isNil(firstItem)) {
         return null;
      }

      if (firstItem?.type !== ConversationTypeRemoteDto.Call) {
         return null;
      }

      if (currentStatusInfo?.type !== StatusType.LOGGED_ON_READY) {
         return null;
      }

      if (firstItem.assignedUserId > 0) {
         return null;
      }

      const skillUserConfiguration = getSkillUserConfigurationWithIdFn(firstItem.queueIdOpExt);
      if (skillUserConfiguration?.userSkillValue < firstItem.skillValue) {
         return null;
      }

      return getIsSharedSkillFn(firstItem.queueIdOpExt)
         ? {
              callId: firstItem.callId,
              peerId: firstItem.peerId,
              peerName: firstItem.peerName,
           }
         : null;
   }

   @Selector([SamwinCallListSelector.getFirstNonParkedItem])
   public static getActiveOrRingingCall(firstItem: SamwinCallInfoRemoteDto): number {
      if (_.isNil(firstItem)) {
         return null;
      }
      if (firstItem?.type !== ConversationTypeRemoteDto.Call) {
         return null;
      }
      if (firstItem.assignedUserId > 0) {
         return firstItem.callId;
      }
      return null;
   }

   @Selector([SamwinCallListSelector.getFirstNonParkedItem, SamwinSkillsState.getIsSharedSkillFn, SamwinUserStatusState.getCurrentStatusInfo])
   public static getActiveOrRingingChatFromSharedSkill(
      firstItem: SamwinCallInfoRemoteDto,
      getIsSharedSkillFn,
      currentStatusInfo: UserStatusInfoRemoteDto
   ): SamwinCallInfoRemoteDto {
      if (_.isNil(firstItem)) {
         return null;
      }

      if (firstItem?.type !== ConversationTypeRemoteDto.RocketChat && firstItem?.type !== ConversationTypeRemoteDto.Chat) {
         return null;
      }

      if (currentStatusInfo?.type !== StatusType.LOGGED_ON_READY) {
         return null;
      }

      if (firstItem.assignedUserId > 0) {
         return null;
      }
      return getIsSharedSkillFn(firstItem.queueIdOpExt) ? firstItem : null;
   }

   @Selector([SamwinCallListState.getCallList])
   public static getMessageIdByCallId(callList: SamwinCallInfoRemoteDto[]): (callId: number) => string {
      return (callId: number) => _.find(callList, (entry) => entry.callId === callId)?.messageId;
   }
}
